import { useCallback, useMemo } from 'react'

import { createColumnHelper } from '@tanstack/react-table'
import { Table } from 'govwell-ui'
import { useTableState } from 'govwell-ui/components/Table/use-table-state'
import { AIChatbotFAQFragment } from 'types/graphql'

import { useSearchAIChatbotFAQsQuery } from 'src/fetch/aiChatbotFAQ'
import { AIChatbotFAQCountColumn } from 'src/pages/AIAssistantTrainPage/columns'
import UnansweredQuestionsTableButtons from 'src/pages/AIAssistantTrainPage/UnansweredQuestionsTableButtons'
import ParsedHtmlMessage from 'src/pages/AIChat/AIResponseMessage/ParsedHtmlMessage'

const UnansweredQuestionsTable = () => {
  const getRowId = useCallback((faq: AIChatbotFAQFragment) => `${faq.id}`, [])
  const { debouncedSearchQuery, ...tableProps } = useTableState<AIChatbotFAQFragment>({
    initialState: { pagination: { pageIndex: 0, pageSize: 10 } },
  })
  const { count, faqs, isFetching } = useSearchAIChatbotFAQsQuery({
    pagination: {
      page: tableProps.pagination.pageIndex + 1,
      pageSize: tableProps.pagination.pageSize,
      query: debouncedSearchQuery,
    },
    type: 'Unanswered',
  })

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<AIChatbotFAQFragment>()
    return [
      AIChatbotFAQCountColumn(columnHelper),
      columnHelper.accessor((faq) => faq.question, {
        id: 'question',
        header: 'Question',
        cell: (info) => <ParsedHtmlMessage className="!w-[300px]" html={info.getValue() ?? ''} />,
        enableSorting: false,
      }),
      columnHelper.display({
        id: 'button',
        header: '',
        meta: { textAlign: 'right', width: '100px', minWidth: '25px' },
        cell: (info) => <UnansweredQuestionsTableButtons aiChatbotFAQ={info.row.original} />,
      }),
    ]
  }, [])

  return (
    <>
      <Table
        title="Unanswered Questions"
        columns={columns}
        data={faqs}
        enablePagination
        enableSearching
        getRowId={getRowId}
        isLoading={isFetching}
        totalCount={count}
        showCount
        emptyStateMessage="You're all caught up! As users interact with the Community Assistant and ask questions, they will be added here for you to answer and train the Community Assistant. Members of the AI team will also be emailed reminders to answer questions and train the Community Assistant."
        {...tableProps}
      />
    </>
  )
}

export default React.memo(UnansweredQuestionsTable)
